import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect hover state on an element
 * @param {React.RefObject} ref - Reference to the DOM element
 * @param {Object} options - Configuration options
 * @param {number} options.enterDelay - Delay in milliseconds before setting hover to true
 * @param {number} options.leaveDelay - Delay in milliseconds before setting hover to false
 * @returns {boolean} - Whether the element is currently being hovered
 */
function useHover(ref, options = {}) {
  const { enterDelay = 0, leaveDelay = 0 } = options;
  const [isHovering, setIsHovering] = useState(false);
  const enterTimeoutRef = useRef(null);
  const leaveTimeoutRef = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseEnter = () => {
      // Clear any pending leave timeout
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }

      // Set hover state after enter delay
      if (enterDelay > 0) {
        enterTimeoutRef.current = setTimeout(() => {
          setIsHovering(true);
        }, enterDelay);
      } else {
        setIsHovering(true);
      }
    };

    const handleMouseLeave = () => {
      // Clear any pending enter timeout
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
        enterTimeoutRef.current = null;
      }

      // Set hover state to false after leave delay
      if (leaveDelay > 0) {
        leaveTimeoutRef.current = setTimeout(() => {
          setIsHovering(false);
        }, leaveDelay);
      } else {
        setIsHovering(false);
      }
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    // eslint-disable-next-line consistent-return
    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);

      // Clean up timeouts
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
      }
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, [ref, enterDelay, leaveDelay]);

  return isHovering;
}

export default useHover;
